import { useMemo, useCallback, useState } from 'react';
import { useQuery } from 'react-query';
import { exportAndDownloadCSV } from '../../utils/csv-helpers';

import {
  useTableFilterSortData,
  useTableFilterSortSearchManager,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { generateStandardFilename } from '@fortress-technology-solutions/fortress-component-library/utils';

import { DEFAULT_SORT_ORDER } from '../../components/Table/constants';
import { PROPERTY_PATH_MAP, HEADERS, NAME } from './constantsV2';
import {
  PORTAL_ACTIVE_NEVER_ONBOARDED,
  PORTAL_ACTIVE_AND_ONBOARDED,
} from './constants';
import useUserOrganizationId from '../../hooks/useUserOrganizationId';
import PropertyService from '../../services/propertyService';
import {
  determinePortalStatus,
  createAvailabilityLink,
  createPortalLink,
  createContactUsLink,
} from './utils';

const useCSVPDFExport = ({
  filteredHeaders,
  excludedHeaders,
  rows,
  hasAnyFilters,
}) => {
  const onCSVButtonClick = useCallback(() => {
    const fileName = generateStandardFilename({
      type: NAME,
      descriptor: 'Manage Properties',
      extension: 'csv',
    });

    exportAndDownloadCSV({
      excludedHeaders,
      filteredHeaders,
      fileName,
      rows,
      hasAnyFilters,
      shouldUseDashes: false,
    });
  }, [rows, excludedHeaders, filteredHeaders, hasAnyFilters]);

  return { onCSVButtonClick };
};

const generateFilterOptions = (results, fieldPath) => {
  if (!results || !results.length) return [];

  return [
    ...new Set(
      results
        .map((result) => {
          const pathParts = fieldPath.split('.');
          let value = result;

          for (const part of pathParts) {
            value = value?.[part];
            if (value === undefined || value === null) break;
          }

          if (fieldPath === 'autoLateFeesPostingActive') {
            return value === true ? 'Yes' : value === false ? 'No' : value;
          }

          return value;
        })
        .filter(Boolean),
    ),
  ]
    .sort()
    .map((value) => ({ text: value, value }));
};

export const useHeaders = ({ intl, results }) => {
  return useMemo(() => {
    const allHeaders = HEADERS({ intl });

    const headerPathMap = {
      ownerName: 'propertyOwner.name',
      propertyType: 'propertyClass.propertyType',
      status: 'propertyStatus.description',
      period: 'pfp.periodName',
      autoLateFeesPostingActive: 'autoLateFeesPostingActive',
      gpr: 'propertyReportConfiguration',
    };

    return allHeaders.map((header) => {
      if (headerPathMap[header.id]) {
        const filterOptions = generateFilterOptions(
          results,
          headerPathMap[header.id],
        );
        return {
          ...header,
          filterOptions,
        };
      }
      return header;
    });
  }, [intl, results]);
};

export const useManageProperties = ({
  intl,
  handleEditPropertyPortal,
  contactUsWidget,
}) => {
  const currentOrganizationId = useUserOrganizationId();

  const { results, isLoading, isSuccess, updateQuery, query } =
    useFetchProperties({
      organizationId: currentOrganizationId,
    });

  const rows = useParsePropertiesResults(
    results,
    handleEditPropertyPortal,
    currentOrganizationId,
    contactUsWidget,
  );

  const headers = useHeaders({ intl, results });

  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name: NAME,
    headers,
    initialOrderBy: 'propertyName',
    initialOrder: 'ASC',
    initialFilterState: {
      status: ['Active'],
    },
  });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name: NAME,
    headers,
    firstRow: rows[0],
  });

  const hasAnyFilters =
    Object.keys(filterState)?.length ||
    Object.keys(dateState)?.length ||
    Object.keys(searchState)?.length;

  const sortedAndFilteredResults = useTableFilterSortData({
    results: rows,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP,
  });

  const { onCSVButtonClick } = useCSVPDFExport({
    filteredHeaders,
    rows: sortedAndFilteredResults,
    hasAnyFilters,
    excludedHeaders: ['links'],
  });

  return {
    allColumnsHidden,
    columnOptions,
    dateState,
    filterState,
    filterTypeState,
    filteredHeaders,
    handleColumnChange,
    handleDateSubmit,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSortChange,
    headers,
    isLoading,
    isSuccess,
    name: NAME,
    onCSVButtonClick,
    order,
    orderBy,
    rows: sortedAndFilteredResults,
    sortedAndFilteredResults,
    searchState,
    selectedColumns,
    count: sortedAndFilteredResults?.length ?? 0,
    totalCount: rows?.length ?? 0,
    updateQuery,
    query,
  };
};

export const useFetchProperties = ({ organizationId }) => {
  const [query, setQuery] = useState({
    sorting: { fieldName: 'name', order: DEFAULT_SORT_ORDER },
    paginationInfo: { pagination: false },
  });

  const updateQuery = useCallback((newQuery) => {
    setQuery({
      ...newQuery,
      paginationInfo: { pagination: false },
    });
  }, []);

  const queryKey = [NAME, organizationId, query];

  let { data, isLoading, isSuccess } = useQuery(
    queryKey,
    () => {
      const abortController = new AbortController(); // eslint-disable-line
      const options = { signal: abortController.signal };
      return new PropertyService().getProperties(
        organizationId,
        query,
        options,
      );
    },
    {
      enabled: Boolean(organizationId),
      refetchOnWindowFocus: false,
    },
  );

  const results = useMemo(() => data?.results ?? [], [data?.results]);

  return { results, isLoading, isSuccess, updateQuery, query };
};

export const useParsePropertiesResults = (
  results,
  handleEditPropertyPortal,
  organizationId,
  contactUsWidget,
) => {
  return useMemo(() => {
    const parsed = results
      ?.map((result) => {
        const periodName = result.pfp?.periodName;
        const periodColumnValue = result.pfp?.isClosePending
          ? `${periodName} (Closing Started)`
          : periodName;

        const portalStatus = determinePortalStatus(result);
        const orgPropUrl = `${organizationId}/${result.id}`;

        const availabilityUrl = createAvailabilityLink(orgPropUrl);
        const portalUrl = createPortalLink(orgPropUrl);
        const contactUsUrl = createContactUsLink(orgPropUrl);

        const resultWithPortalStatus = {
          ...result,
          portalStatus: portalStatus,
        };

        const gprValue = result.propertyReportConfiguration ?? '---';

        let portalActive = { value: undefined };
        if (portalStatus === 'In Process') {
          portalActive.value = 'In Process';
        } else if (
          [PORTAL_ACTIVE_NEVER_ONBOARDED, PORTAL_ACTIVE_AND_ONBOARDED].includes(
            portalStatus,
          )
        ) {
          portalActive.variant = 'button';
          portalActive.onClick = () =>
            handleEditPropertyPortal(resultWithPortalStatus);
          portalActive.value = 'Yes';
        } else {
          portalActive.variant = 'button';
          portalActive.onClick = () =>
            handleEditPropertyPortal(resultWithPortalStatus);
          portalActive.value = 'No';
        }

        const linksArray = [];

        linksArray.push(
          ...[
            {
              value: 'Availability',
              href: availabilityUrl,
              target: '_blank',
            },
            {
              value: 'Portal',
              href: portalUrl,
              target: '_blank',
            },
            {
              value: 'Contact Us',
              href: contactUsUrl,
              target: '_blank',
            },
          ],
        );

        return {
          status: {
            value: result.propertyStatus?.description,
          },
          propertyName: {
            variant: 'link',
            value: result.name,
            to: `/property/${result.id}/?tab=6`,
          },
          shortName: {
            value: result.shortName,
          },
          ownerName: {
            value: result.propertyOwner?.name,
          },
          propertyType: {
            value: result.propertyClass?.propertyType,
          },
          address: {
            value: `${result.physicalStreetAddress1 || ''} ${
              result.physicalCity || ''
            }, ${result.state?.code || ''} ${result.physicalZip || ''}`.trim(),
          },
          phone: {
            variant: 'phoneNumber',
            value: result.phoneNumber,
          },
          email: { variant: 'email', value: result.propertyEmail },
          period: { value: periodColumnValue },
          autoLateFeesPostingActive: {
            value: result.autoLateFeesPostingActive,
          },
          leaseVariance: {
            variant: 'number',
            value: result.leaseRentPercentage
              ? `${result.leaseRentPercentage.replace('%', '') + '%'}`
              : undefined,
          },
          gpr: {
            variant: 'number',
            value: gprValue,
          },
          portalActive: portalActive,
          links: {
            variant: 'links',
            array: linksArray,
          },
          portalStatus: { value: portalStatus },
        };
      })
      .filter(Boolean);

    return parsed;
  }, [results, handleEditPropertyPortal, organizationId, contactUsWidget]);
};
